import { useCallback, useEffect, useState } from 'react'
import { BarChart3, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import {
  exportExpiringPackagesReport,
  fetchExpiringPackagesReport,
  formatDaysUntilExpiry,
  formatReportDate,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPreferences } from '@/contexts/ReportPreferencesContext'
import { posPackageTypeLabel } from '@/lib/pos/posPackage'
import { cn } from '@/lib/utils'

const WINDOW_OPTIONS = [7, 30, 90]

export function ExpiringPackagesReportPanel() {
  const { activeBranchId } = useBranch()
  const { expiringWindowDays, setExpiringWindowDays } = useReportPreferences()
  const windowDays = expiringWindowDays
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const loadReport = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const data = await fetchExpiringPackagesReport(windowDays)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load expiring packages report.'))
    } finally {
      setLoading(false)
    }
  }, [windowDays])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportExpiringPackagesReport(format, windowDays)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <BarChart3 className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Expiring packages</h2>
            <p className="text-sm text-muted-foreground">
              Active customer packages expiring within the selected window.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {WINDOW_OPTIONS.map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => setExpiringWindowDays(days)}
              className={cn(
                'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
                windowDays === days
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/70 hover:bg-muted',
              )}
            >
              {days} days
            </button>
          ))}
          <button
            type="button"
            onClick={() => handleExport('csv')}
            disabled={exporting || loading}
            className="h-9 rounded-xl border border-border/80 bg-card px-3 text-sm disabled:opacity-50"
          >
            CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={exporting || loading}
            className="h-9 rounded-xl border border-border/80 bg-card px-3 text-sm disabled:opacity-50"
          >
            PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="py-4 text-sm text-destructive" role="alert">{error}</p>
      ) : (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Expiring</p>
              <p className="mt-1 text-2xl font-semibold">{report?.summary?.total_expiring ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Within 7 days</p>
              <p className="mt-1 text-2xl font-semibold">{report?.summary?.expiring_within_7_days ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">With balance</p>
              <p className="mt-1 text-2xl font-semibold">{report?.summary?.with_balance ?? 0}</p>
            </div>
          </div>

          {report?.packages?.length ? (
            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Package</th>
                    <th className="px-4 py-3 font-medium">Expires</th>
                    <th className="px-4 py-3 font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {report.packages.map((row) => (
                    <tr key={row.customer_package_id} className="border-t border-border/50">
                      <td className="px-4 py-3">
                        <Link
                          to="/customers"
                          className="font-medium text-primary hover:underline"
                        >
                          {row.customer_name}
                        </Link>
                        <div className="text-xs text-muted-foreground">{row.customer_phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{row.package_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {posPackageTypeLabel(row.package_type)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>{formatReportDate(row.expires_at)}</div>
                        <div className={cn(
                          'text-xs',
                          (row.days_until_expiry ?? 99) <= 7
                            ? 'font-medium text-amber-700'
                            : 'text-muted-foreground',
                        )}
                        >
                          {formatDaysUntilExpiry(row.days_until_expiry)}
                        </div>
                      </td>
                      <td className="px-4 py-3">{row.headline}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground">
              No active packages expire in the next {windowDays} days.
            </p>
          )}
        </>
      )}
    </section>
  )
}
